import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { DateRange } from 'react-day-picker';

import DatetimeRangePickerView from './DatetimeRangePicker.view';

import { DatePickerFormats } from 'utils/enums/time-format';
import {
  DateRangeStepType,
  OnClick,
  TypesFilterButton,
} from 'views/MTable/TableFilters/MDateRangeFilterButton/MDateRangeFilterButton.model';
import changeDate, { DateRangeStepDirections } from 'views/MTable/TableFilters/MDateRangeFilterButton/MDateRangeFilterButton.utils';
import useFlaggedCallback from 'utils/hooks/use-flagged-callback';
import { TimeRange, fullDayTimeRange } from 'utils/time';

type Props = {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  hideTimeSelect?: boolean;
  dateFormat?: DatePickerFormats;
  disabled?: boolean;
  showNavigator?: boolean;
  showResetDate?: boolean;
  dateRangeStepType?: DateRangeStepType;
  onResetDate?: () => void;
  onChange: (startDate: moment.Moment | null, endDate: moment.Moment | null) => void;
};

const DatetimeRangePicker: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const [isOpenState, setOpenState] = useState(false);
  const [isDatePickerState, setDatePickerState] = useState(false);
  const [pickerDateRangeState, setPickerDateRangeState] = useState<DateRange | undefined>(undefined);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpenState && (dropdownRef.current || datePickerRef.current)) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          (dropdownRef.current || datePickerRef.current) &&
          (dropdownRef.current !== event.target || datePickerRef.current !== event.target) &&
          (!dropdownRef.current?.contains(event.target as HTMLElement) || !datePickerRef.current?.contains(event.target as HTMLElement))
        ) {
          setOpenState(() => false);
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpenState, dropdownRef, setOpenState]);

  const dateRangeState: DateRange | undefined = useMemo(() => {
    return {
      from: props.startDate?.toDate(),
      to: props.endDate?.toDate(),
    };
  }, [props.startDate, props.endDate]);

  const timeRangeState: TimeRange = useMemo(() => {
    if (!props.startDate || !props.endDate) {
      return fullDayTimeRange;
    }

    return {
      from: props.startDate.format(DatePickerFormats.Time),
      to: props.endDate.format(DatePickerFormats.Time),
    };
  }, [props.startDate, props.endDate]);

  const onPrev = useFlaggedCallback(() => {
    if (!dateRangeState) {
      return;
    }

    const date = dateRangeState;

    changeDate(props.dateRangeStepType!, date!, DateRangeStepDirections.Prev);

    onUpdateDateTime(date, timeRangeState);
  }, [props.dateRangeStepType, dateRangeState /** onUpdateDateTime */]);

  const onNext = useFlaggedCallback(() => {
    if (!dateRangeState && !props.dateRangeStepType) {
      return;
    }

    const date = dateRangeState;

    changeDate(props.dateRangeStepType!, date!, DateRangeStepDirections.Next);

    onUpdateDateTime(date, timeRangeState);
  }, [props.dateRangeStepType, dateRangeState /** onUpdateDateTime */]);

  const onUpdateDateTime = (dateRange: DateRange | undefined, timeRange: TimeRange) => {
    setPickerDateRangeState(() => dateRange);

    const [fromHours, fromMinutes] = timeRange.from.split(':');
    const [toHours, toMinutes] = timeRange.to.split(':');

    const value = {
      startDate: moment(dateRange?.from).set({ hours: Number(fromHours), minutes: Number(fromMinutes) }),
      endDate: dateRange?.to
        ? moment(dateRange?.to).set({ hours: Number(toHours), minutes: Number(toMinutes) })
        : moment(dateRange?.from).set({ hours: Number(toHours), minutes: Number(toMinutes) }),
    };

    props.onChange(value.startDate, value.endDate);
  };

  const onChangeDate = (type: TypesFilterButton) => {
    switch (type) {
      case TypesFilterButton.Today:
        onUpdateDateTime(
          {
            from: moment().startOf('day').toDate(),
            to: moment().endOf('day').toDate(),
          },
          fullDayTimeRange,
        );

        break;
      case TypesFilterButton.Tomorrow:
        onUpdateDateTime(
          {
            from: moment().add({ days: 1 }).toDate(),
            to: moment().add({ days: 1 }).toDate(),
          },
          fullDayTimeRange,
        );

        break;
      case TypesFilterButton.Yesterday:
        onUpdateDateTime(
          {
            from: moment().add({ days: -1 }).toDate(),
            to: moment().add({ days: -1 }).toDate(),
          },
          fullDayTimeRange,
        );

        break;
      case TypesFilterButton.Week:
        onUpdateDateTime(
          {
            from: moment().add({ weeks: -1 }).toDate(),
            to: moment().toDate(),
          },
          fullDayTimeRange,
        );

        break;
      case TypesFilterButton.Month:
        onUpdateDateTime(
          {
            from: moment().add({ months: -1 }).toDate(),
            to: moment().toDate(),
          },
          fullDayTimeRange,
        );

        break;
    }
  };

  return (
    <DatetimeRangePickerView
      onOpen={() => {
        setOpenState((prev) => !prev);
        setDatePickerState(() => false);
        setPickerDateRangeState(() => undefined);
      }}
      dateRange={dateRangeState}
      pickerDateRange={isDatePickerState ? pickerDateRangeState : dateRangeState}
      timeRange={timeRangeState}
      onResetDate={props.onResetDate}
      dateRangeStepType={props.dateRangeStepType}
      onPrevDate={onPrev}
      onNextDate={onNext}
      isOpen={isOpenState}
      onChangeTypePicker={(event: OnClick) => {
        event.stopPropagation();
        setDatePickerState((prev) => {
          const nextState = !prev;

          if (nextState) {
            setPickerDateRangeState(() => undefined);
          }

          return nextState;
        });
      }}
      onClose={() => setOpenState(() => false)}
      typePicker={isDatePickerState}
      datePickerRef={datePickerRef}
      dropdownRef={dropdownRef}
      updateDateTimeRange={onUpdateDateTime}
      onChangeDate={onChangeDate}
    ></DatetimeRangePickerView>
  );
};

DatetimeRangePicker.displayName = 'DatetimeRangePicker';
DatetimeRangePicker.defaultProps = {
  dateRangeStepType: DateRangeStepType.Day,
};

export default DatetimeRangePicker;
